import styled, { css } from 'styled-components';

import VdpBox from '../../../_shared/components/VdpBox';
import Detail from '../../../_shared/components/Detail';
import Box from '../../../_shared/components/Box';
import Label from '../../../_shared/components/Label';
import Badge from '../../../_shared/components/Badge';
import Icon from '../../../_shared/components/Icon';
import applyResponsiveVdpStyles from '../../../_shared/helpers/applyResponsiveVdpStyles';

/**
 * Variant Definitions
 */
const StyledTransportDetailsVariants = {
  mini: css`
    width: 100%;
  `,
  expanded: css`
    width: 49%;
  `
};

const StyledTransportHeadingContainerVariants = {
  mini: css`
    margin-bottom: 15px;
  `
};

const StyledTransportHeadingVariants = {
  mini: css`
    padding: 0 2px;
  `
};

const StyledDetailVariants = {
  mini: css`
    display: block;
  `,
  expanded: css`
    padding-right: 40px;

    &:last-child {
      padding-right: 0;
    }
  `
};

const StyledBoxVariants = {
  mini: css`
    flex-direction: column;
    padding-top: 11px;
  `,
  expanded: css`
    padding: 44px 0 12px;
  `
};

const StyledDiscAndDateExpectedVariants = {
  mini: css`
    flex-direction: row;
    flex-wrap: nowrap;
    width: 100%;
    column-gap: 18px;
    & > div:first-child {
      padding-bottom: 25px;
    }
  `,
  expanded: css`
    flex-direction: column;
    width: 40%;

    & > div:first-child {
      padding-bottom: 36px;
    }
  `
};

const StyledEstimateAndLocationVariants = {
  mini: css`
    column-gap: 18px;
    width: 100%;
  `,
  expanded: css`
    width: 60%;
  `
};

const StyledRateInfoVariants = {
  mini: css`
    margin-top: 25px;
  `,
  expanded: css`
    margin-top: 7px;
  `
};

const StyledQuestionIconVariants = {
  mini: css`
    height: 18px;
    width: 18px;
    margin-left: 5px;
  `,
  expanded: css`
    height: 24px;
    width: 24px;
  `
};

/**
 * Styled Components
 */
export const StyledTransportDetails = styled(VdpBox)`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0 24px 0 0;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportDetailsVariants.mini,
      expanded: StyledTransportDetailsVariants.expanded
    })}
`;

export const StyledTransportHeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportHeadingContainerVariants.mini
    })}
`;

export const StyledTransportHeading = styled.h2`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTransportHeadingVariants.mini
    })}
`;

export const StyledTransportCountBadge = styled(Badge)`
  border: 2px solid ${({ theme }) => theme.COLORS.ORANGE};
  color: ${({ theme }) => theme.COLORS.ORANGE};
  min-width: 36px;
  border-radius: 18px;
  text-align: center;
  margin: 0 12px;
  max-height: ${({ theme }) => theme.FONT_SIZES.LARGE};
`;

export const StyledDetail = styled(Detail)`
  width: 100%;
  min-height: 40px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDetailVariants.mini,
      expanded: StyledDetailVariants.expanded
    })}
`;

export const StyledBox = styled(Box)`
  display: flex;
  width: 100%;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledBoxVariants.mini,
      expanded: StyledBoxVariants.expanded
    })}
`;

export const StyledDiscAndDateExpected = styled(Box)`
  display: flex;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDiscAndDateExpectedVariants.mini,
      expanded: StyledDiscAndDateExpectedVariants.expanded
    })}
`;

export const StyledEstimateAndLocation = styled(Box)`
  display: flex;
  flex-direction: row;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledEstimateAndLocationVariants.mini,
      expanded: StyledEstimateAndLocationVariants.expanded
    })}
`;

export const StyledIndicator = styled.div`
  width: 19px;
  height: 0;
  border: 4px solid #f95341;
  transform: rotate(90deg);
`;

export const StyledDetailLabel = styled(Detail.Value)`
  position: relative;
  &:before {
    position: absolute;
    left: -3px;
    content: ' ';
    border-radius: 3px;
    background: ${({ theme }) => theme.COLORS.ORANGE};
    width: 4px;
    height: 100%;
  }
`;

export const StyledDetailLabelInner = styled.span`
  margin-left: 10px;
  padding-bottom: 12px;

  &:only-child {
    padding-bottom: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }
`;

export const StyledTransportValue = styled.span`
  margin-left: auto;
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
`;

export const StyledPromoOnIndicator = styled.div`
  display: block;
  width: 128px;
  height: 32px;
  background: #82bc00;
  border-radius: 3px;
  margin-top: 9px;
`;

export const StyledPromoOnIndicatorLabel = styled(Label.Bold)`
    font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
    font-size: ${({ theme }) => theme.FONT_SIZES.BASE_SIZE};
    color: ${({ theme }) => theme.COLORS.BASE};
    line-height: 16px;
    margin-top: 7px;
    margin-left: 10px;
    padding-top: 8px;
    padding-left: 5px;
  }
`;

export const StyledRateInfo = styled.a`
  display: inline-block;
  margin-left: auto;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
  cursor: pointer;
  color: inherit;

  &:hover {
    color: ${({ theme }) => theme.COLORS.DARK_BLUE};
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledRateInfoVariants.mini,
      expanded: StyledRateInfoVariants.expanded
    })}
`;

export const StyledDetailValue = styled(Detail.Value)`
  display: block;
  word-break: keep-all;
  padding-right: 10px;
  overflow-wrap: normal;
`;

export const StyledTransportDriveAwayDisc = styled.a`
  display: flex;
  flex-direction: row;
  text-decoration: underline;
  text-decoration-color: ${({ theme }) => theme.COLORS.ACTION_BLUE};
  color: ${({ theme }) => theme.COLORS.ACTION_BLUE};
  cursor: pointer;
  margin-left: 10px;
  padding-bottom: 12px;

  &:only-child,
  &:last-child {
    padding-bottom: 0;
  }

  &:hover {
    color: ${({ theme }) => theme.COLORS.DARK_BLUE};
  }
`;

export const StyledQuestionIcon = styled(Icon)`
  margin-right: 18px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledQuestionIconVariants.mini,
      expanded: StyledQuestionIconVariants.expanded
    })}
`;
